import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { adminCatalogApi, adminCatalogKey } from '@/lib/admin-catalog-api';
import { getErrorMessage } from '@/lib/errors';
import type { Product } from '@/types/api';

/**
 * Create or edit a product.
 *
 * A product is the thing being sold; plans are the ways to buy it. Most operators need
 * exactly one product, which is why this dialog is deliberately small — the interesting
 * decisions all live on the plan.
 */
export function ProductDialog({
  product,
  onClose,
}: {
  /** Null to create. */
  product: Product | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(product?.name ?? '');
  const [slug, setSlug] = useState(product?.slug ?? '');
  const [description, setDescription] = useState(product?.description ?? '');

  const save = useMutation({
    mutationFn: () => {
      const input = {
        name: name.trim(),
        // Omitted rather than sent blank when creating: the API derives a slug from the
        // name, and an empty string would be taken as a deliberate choice.
        ...(slug.trim() === '' ? {} : { slug: slug.trim() }),
        description: description.trim(),
      };

      return product === null
        ? adminCatalogApi.createProduct(input)
        : adminCatalogApi.updateProduct(product.id, input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: adminCatalogKey });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{product === null ? 'New product' : 'Edit product'}</DialogTitle>
          <DialogDescription>
            The thing you sell. Plans underneath it are how customers buy it.
          </DialogDescription>
        </DialogHeader>

        {save.isError && <Alert variant="danger">{getErrorMessage(save.error)}</Alert>}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="product-name">Name</Label>
            <Input
              id="product-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={150}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-slug">
              Slug <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="product-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              maxLength={100}
              className="font-mono text-sm"
            />
            {product !== null ? (
              <p className="text-xs text-muted-foreground">
                Changing this changes how the product is referred to elsewhere. Leave it alone
                unless you have a reason.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Leave empty to derive it from the name.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-description">
              Description <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="product-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={5000}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || name.trim() === ''}>
            {save.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {product === null ? 'Create product' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
